import type { PromotionType } from '@/states/promotion-state';

export type JsonValue = string | number | boolean | null | JsonObject | JsonValue[];
export interface JsonObject {
  [key: string]: JsonValue;
}
export type ImportFormat = string;
export interface ImportProvider {
  key: string;
  name: string;
  providerKey: string;
  payloadKind: 'Json' | 'Xml';
}
export type FieldSource = 'Raw' | 'Normalized' | 'Effective';
export type ScalarType = 'String' | 'Number' | 'Boolean' | 'Date';
export type RuleKind = 'Condition' | 'All' | 'Any' | 'AnyElement';
export type RuleOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'startsWith'
  | 'in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'isMissing'
  | 'isPresent';

export type ProviderMode = 'Legacy' | 'StagedImport';
export interface ImportedJobSummary {
  id: string;
  companyId: number;
  companyName: string;
  serviceId: number;
  format: ImportFormat;
  sourceId: string;
  title: string;
  sourceState: string;
  evaluationState: string;
  processingState: string;
  approvalState: string;
  reviewRequired: boolean;
  issueCount: number;
  // The server's Needs attention reason; null when the import needs no attention.
  attentionReason?: string | null;
  lastSeenAt: string;
  updatedAt: string;
  jobAdId: number | null;
  version: string;
}

export interface ImportedJobDetail extends ImportedJobSummary {
  supportsDefaultPromotion: boolean;
  defaultPromotionType: PromotionType | null;
  providerMode: ProviderMode;
  canMutate: boolean;
  requiresSourceRefresh: boolean;
  matrixTags: {
    classifiedAt: string;
    hasExactTags: boolean;
    tags: { id: string; label: string; score: number }[];
  } | null;
  rawPayload: string;
  normalized: JsonObject;
  effective: JsonObject;
  corrections: JsonObject;
  issues: { code: string; field: string | null; message: string }[];
  reasons: string[];
  attemptCount: number;
  lastAttemptAt: string | null;
  lastError: string | null;
  approvedAt: string | null;
  approvedBy: string | null;
  linkedJobAd: {
    id: number;
    publicId: string | null;
    state: string;
    publishedDate: string | null;
    lastPublishDate: string | null;
  } | null;
  canApprove: boolean;
  canRetry: boolean;
  canChoosePromotion: boolean;
  promotionChoice: { promotion: PromotionType; chosenBy: string | null; chosenAt: string } | null;
  promotionCredits: { promotion: PromotionType; amount: number }[];
}

export interface ImportConnection {
  serviceId: number;
  companyId: number;
  companyName: string;
  name: string;
  format: ImportFormat;
  active: boolean;
}

export interface FieldDescriptor {
  source: 'raw' | 'normalized' | 'effective';
  path: string;
  valueType: 'string' | 'number' | 'boolean' | 'date' | 'object' | 'array';
  samples: JsonValue[];
  editable: boolean;
  label: string | null;
  options?: { value: JsonValue; label: string }[] | null;
}

export interface RuleNode {
  kind: RuleKind;
  source?: FieldSource;
  path?: string;
  valueType?: ScalarType;
  operator?: RuleOperator;
  value?: JsonValue;
  children?: RuleNode[];
}
export interface RuleSet {
  root: RuleNode | null;
}
export interface ImportRules {
  canMutate: boolean;
  requiresConnectionDate?: boolean;
  defaultContext?: string | null;
  format: ImportFormat;
  serviceId: number | null;
  defaultRules: RuleSet;
  customRules: RuleSet | null;
  effectiveRules: RuleSet;
  manualApproval: boolean;
  version: string;
}
export interface RulePreview {
  total: number;
  matches: number;
  excluded: number;
  // Excluded records whose job is live: saving the rules withdraws those jobs.
  liveExcluded: number;
  data: { id: string; title: string; sourceId: string; matches: boolean; reasons: string[] }[];
  errors: string[];
}
export interface ImportFilters {
  view: 'attention' | 'all';
  search: string;
  format: string;
  company: string;
  status: string;
  // Needs attention only: the attention reason compartment the list is narrowed to.
  reason: string;
  page: number;
}
export interface CorrectionDraft {
  mode: 'value' | 'null' | 'reset';
  text: string;
}

export const scalarTypes: ScalarType[] = ['String', 'Number', 'Boolean', 'Date'];
export const fieldSources: FieldSource[] = ['Raw', 'Normalized', 'Effective'];
export const ruleKinds: RuleKind[] = ['Condition', 'All', 'Any', 'AnyElement'];
export const ruleOperators: RuleOperator[] = [
  'equals',
  'notEquals',
  'contains',
  'startsWith',
  'in',
  'gt',
  'gte',
  'lt',
  'lte',
  'isMissing',
  'isPresent',
];
